import { useTranslation } from "react-i18next";

import type { Paper, Project } from "../../api/types";
import { CardLabel } from "./primitives/DetailsCard";

export function DashboardPanel({ projects }: { projects: Project[] }) {
  const { t } = useTranslation();
  const papers: Paper[] = projects.flatMap((p) => p.papers);
  const count = (pred: (p: Paper) => boolean) => papers.filter(pred).length;

  const completed = count((p) => p.status === "completed");
  const inReview = count((p) => p.status === "in_review" || p.status === "ready");
  const processing = count((p) => p.status === "staged" || p.status === "processing");
  const pending = count((p) => p.status === "queued");
  const failed = count((p) => p.status === "failed" || p.status === "quota_exceeded");

  return (
    <div className="details-content">
      <div className="details-title">{t("dashboard.title")}</div>
      <div className="details-subtitle">{t("dashboard.subtitle")}</div>

      <div className="dash-grid">
        <div className="dash-card">
          <div className="dash-card-value">{projects.length}</div>
          <div className="dash-card-label">{t("dashboard.projects_label")}</div>
        </div>
        <div className="dash-card">
          <div className="dash-card-value">{papers.length}</div>
          <div className="dash-card-label">{t("dashboard.papers_label")}</div>
        </div>
      </div>

      <CardLabel>{t("dashboard.status_breakdown")}</CardLabel>
      <div className="details-card dash-status-card">
        <StatusRow variant="completed" name={t("paper_status.completed")} count={completed} />
        <StatusRow variant="in-review" name={t("paper_status.in_review")} count={inReview} />
        <StatusRow variant="processing" name={t("paper_status.processing")} count={processing} />
        <StatusRow variant="pending" name={t("paper_status.queued")} count={pending} />
        {failed > 0 && <StatusRow variant="pending" name={t("paper_status.failed")} count={failed} />}
      </div>
    </div>
  );
}

function StatusRow({ variant, name, count }: { variant: string; name: string; count: number }) {
  return (
    <div className="dash-status-row">
      <span className={`detail-dot detail-dot--${variant}`} />
      <span className="dash-status-name">{name}</span>
      <span className="dash-status-count">{count}</span>
    </div>
  );
}
